import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from './AuthContext.jsx'
import { dexApi } from '../lib/apiBase.js'
import { dexFetch } from '../lib/dexApi.js'
import { isPlatformAvailabilityFailure } from '../lib/cexApi.js'

const API_GROUPS = '/api/groups'
const GROUPS_STORAGE_KEY_PREFIX = 'lotos_dashboard_groups::'
const GROUPS_STORAGE_LEGACY_KEY = 'lotos_dashboard_groups'
/** Fallback poll when SSE unavailable; primary updates via /api/dashboard/events */
const SYNC_INTERVAL_MS = 20_000
/** Do not overwrite local edits with a fresh GET immediately after a change (ms). After the pause, pull server for multi-user sync. */
const SYNC_LOCAL_GRACE_MS = 4000
const PERSIST_RETRIES = 2
const PERSIST_RETRY_DELAY_MS = 80

function storageKeyForUser(user) {
  // Scope localStorage per user so two tabs with different accounts can't
  // contaminate each other's view via the `storage` event.
  const uid = user?.id ? String(user.id) : 'anon'
  return `${GROUPS_STORAGE_KEY_PREFIX}${uid}`
}

function writeGroupsToStorage(list, user) {
  if (!list || !Array.isArray(list)) return
  try {
    localStorage.setItem(storageKeyForUser(user), JSON.stringify(list))
  } catch (_) {}
}

function cleanupLegacyGroupsStorage() {
  // Pre-multi-user deployments used a single shared key, which caused visible
  // groups to "disappear" when a second user with a narrower scope wrote to it.
  try { localStorage.removeItem(GROUPS_STORAGE_LEGACY_KEY) } catch (_) {}
}

function normalizeGroupsPayload(data) {
  if (!Array.isArray(data)) return null
  return data.map((g) => ({ ...g, bots: g.bots || [], wallets: g.wallets || [] }))
}

function isClientRole(user) {
  return String(user?.role ?? '').toLowerCase() === 'client'
}

function isTraderRole(user) {
  return String(user?.role ?? '').toLowerCase() === 'trader'
}

/** Shallow-merge bot updates; merge `config` into existing so partial patches never drop walletId / volume fields. */
function mergeBotUpdates(b, updates) {
  if (!updates || typeof updates !== 'object') return { ...b, ...updates }
  const { config: cfgPatch, ...rest } = updates
  const next = { ...b, ...rest }
  if (cfgPatch !== undefined) {
    next.config = { ...(b.config || {}), ...cfgPatch }
  }
  return next
}

const GroupsContext = createContext(null)

export function GroupsProvider({ children }) {
  const { getAuthHeaders, user, authInitialized } = useAuth()
  // Always start with empty list; data from server only (not localStorage) so reload does not send stale/empty cache
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [syncError, setSyncError] = useState(null)
  const isMounted = useRef(true)
  const groupsRef = useRef([])
  const saveInFlightRef = useRef(false)
  const pendingSaveRef = useRef(false)
  const savingRef = useRef(false)
  const hasLoadedOnceRef = useRef(false)
  const dirtyRef = useRef(false)
  const lastLocalMutationAtRef = useRef(0)
  const groupsSigRef = useRef('[]')
  const lastGroupsRevRef = useRef(0)
  const lastWarmupSettingsRevRef = useRef(0)
  const lastExchangeDistributorDraftRevRef = useRef(0)
  const lastExchangeDistributorArchiveRevRef = useRef(0)
  const dashboardEsRef = useRef(null)
  const dashboardEsReconnectRef = useRef(null)
  // Tracked so the client can skip its own echo from SSE `groups` events
  // (the server now broadcasts `originSessionId`; a matching id means the
  // update came from this tab and our local state is already authoritative).
  const sseSessionIdRef = useRef(null)
  const userIdRef = useRef(null)

  useEffect(() => {
    groupsRef.current = groups
    try {
      groupsSigRef.current = JSON.stringify(groups || [])
    } catch {
      groupsSigRef.current = '[]'
    }
  }, [groups])
  useEffect(() => {
    savingRef.current = saving
  }, [saving])
  useEffect(() => {
    userIdRef.current = user?.id || null
    cleanupLegacyGroupsStorage()
  }, [user?.id])

  const loadFromApi = useCallback(async () => {
    try {
      const res = await dexFetch(API_GROUPS, {
        credentials: 'include',
        headers: getAuthHeaders(),
        cache: 'no-store',
      })
      if (res.status === 401) {
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('auth:sessionInvalid'))
        if (isMounted.current) {
          setSyncError(null)
          setLoading(false)
        }
        return
      }
      if (!res.ok) throw new Error(res.statusText)
      const data = await res.json()
      const normalized = normalizeGroupsPayload(data)
      if (!normalized) return
      if (isMounted.current) {
        if (saveInFlightRef.current) return
        if (
          dirtyRef.current &&
          Date.now() - lastLocalMutationAtRef.current < SYNC_LOCAL_GRACE_MS
        ) {
          return
        }
        let nextSig = ''
        try {
          nextSig = JSON.stringify(normalized)
        } catch {
          nextSig = ''
        }
        if (nextSig && nextSig === groupsSigRef.current) {
          setSyncError(null)
          hasLoadedOnceRef.current = true
          dirtyRef.current = false
          return
        }
        // Safety net: for admin/CEO, an unexpected empty payload while we
        // already have groups locally is almost certainly a transient
        // server / auth hiccup, not a legitimate "everything got deleted".
        // Keep the previous state until the next poll confirms.
        const role = String(user?.role ?? '').toLowerCase()
        const canSeeAll = role === 'admin' || role === 'ceo'
        if (
          canSeeAll &&
          normalized.length === 0 &&
          Array.isArray(groupsRef.current) &&
          groupsRef.current.length > 0
        ) {
          setSyncError(null)
          hasLoadedOnceRef.current = true
          return
        }
        setGroups(normalized)
        setSyncError(null)
        writeGroupsToStorage(normalized, user)
        hasLoadedOnceRef.current = true
        dirtyRef.current = false
      }
    } catch (e) {
      if (isMounted.current) {
        if (isPlatformAvailabilityFailure(e)) setSyncError(null)
        else setSyncError(e.message || 'Load error')
      }
    } finally {
      if (isMounted.current) setLoading(false)
    }
  }, [getAuthHeaders, user])

  useEffect(() => {
    const onAccessUpdated = () => {
      dirtyRef.current = false
      loadFromApi()
    }
    window.addEventListener('lotos:accessUpdated', onAccessUpdated)
    return () => window.removeEventListener('lotos:accessUpdated', onAccessUpdated)
  }, [loadFromApi])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  // Load groups only after auth init; refetch on user change. On logout clear local data.
  useEffect(() => {
    if (!authInitialized) return
    hasLoadedOnceRef.current = false
    dirtyRef.current = false
    cleanupLegacyGroupsStorage()
    if (!user) {
      setGroups([])
      setLoading(false)
      // Don't try to wipe every per-user key — this tab just doesn't have
      // a user right now. Leaving other users' data cached is the correct
      // behavior (they're in their own tabs).
      return
    }
    if (isClientRole(user)) {
      setGroups([])
      setLoading(true)
      try {
        localStorage.removeItem(storageKeyForUser(user))
      } catch (_) {}
    }
    loadFromApi()
  }, [authInitialized, loadFromApi, user])

  useEffect(() => {
    if (!authInitialized || !user?.id) {
      lastGroupsRevRef.current = 0
      lastWarmupSettingsRevRef.current = 0
      lastExchangeDistributorDraftRevRef.current = 0
      lastExchangeDistributorArchiveRevRef.current = 0
      if (dashboardEsRef.current) {
        try {
          dashboardEsRef.current.close()
        } catch (_) {}
        dashboardEsRef.current = null
      }
      if (dashboardEsReconnectRef.current) {
        clearTimeout(dashboardEsReconnectRef.current)
        dashboardEsReconnectRef.current = null
      }
      return
    }

    if (typeof EventSource === 'undefined') return

    let reconnectAttempt = 0

    const clearReconnect = () => {
      if (dashboardEsReconnectRef.current) {
        clearTimeout(dashboardEsReconnectRef.current)
        dashboardEsReconnectRef.current = null
      }
    }

    const connect = () => {
      clearReconnect()
      try {
        if (dashboardEsRef.current) {
          dashboardEsRef.current.close()
          dashboardEsRef.current = null
        }
      } catch (_) {}

      const es = new EventSource(dexApi('/api/dashboard/events'), { withCredentials: true })
      dashboardEsRef.current = es

      es.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          if (msg.type === 'hello') {
            if (typeof msg.sessionId === 'string' && msg.sessionId) {
              sseSessionIdRef.current = msg.sessionId
            }
            if (typeof msg.groupsRev === 'number') lastGroupsRevRef.current = msg.groupsRev
            if (typeof msg.warmupSettingsRev === 'number') lastWarmupSettingsRevRef.current = msg.warmupSettingsRev
            if (typeof msg.exchangeDistributorDraftRev === 'number') {
              lastExchangeDistributorDraftRevRef.current = msg.exchangeDistributorDraftRev
            }
            if (typeof msg.exchangeDistributorArchiveRev === 'number') {
              lastExchangeDistributorArchiveRevRef.current = msg.exchangeDistributorArchiveRev
            }
            return
          }
          if (msg.type === 'warmupSettings' && typeof msg.rev === 'number') {
            if (msg.rev > lastWarmupSettingsRevRef.current) {
              lastWarmupSettingsRevRef.current = msg.rev
              window.dispatchEvent(new CustomEvent('lotos:warmupSettingsChanged', { detail: msg }))
            }
            return
          }
          if (msg.type === 'exchangeDistributorDraft' && typeof msg.rev === 'number') {
            if (msg.rev > lastExchangeDistributorDraftRevRef.current) {
              lastExchangeDistributorDraftRevRef.current = msg.rev
              window.dispatchEvent(new CustomEvent('lotos:exchangeDistributorDraftChanged', { detail: msg }))
            }
            return
          }
          if (msg.type === 'exchangeDistributorArchive' && typeof msg.rev === 'number') {
            if (msg.rev > lastExchangeDistributorArchiveRevRef.current) {
              lastExchangeDistributorArchiveRevRef.current = msg.rev
              window.dispatchEvent(new CustomEvent('lotos:exchangeDistributorArchiveChanged', { detail: msg }))
            }
            return
          }
          if (msg.type === 'groups' && typeof msg.rev === 'number') {
            if (msg.rev > lastGroupsRevRef.current) {
              lastGroupsRevRef.current = msg.rev
              // Ignore self-echo: the write came from this tab, so state is
              // already up to date. Also debounce rapid sequences.
              const fromSelf =
                msg.originSessionId &&
                sseSessionIdRef.current &&
                msg.originSessionId === sseSessionIdRef.current
              if (!fromSelf) loadFromApi()
            }
            return
          }
          if (msg.type === 'botStatus') {
            window.dispatchEvent(new CustomEvent('lotos:botStatusInvalidate', { detail: msg }))
          }
        } catch (_) {}
      }

      es.onerror = () => {
        try {
          es.close()
        } catch (_) {}
        if (dashboardEsRef.current === es) dashboardEsRef.current = null
        reconnectAttempt += 1
        const delay = Math.min(30_000, 800 * 2 ** Math.min(reconnectAttempt, 6))
        clearReconnect()
        dashboardEsReconnectRef.current = window.setTimeout(connect, delay)
      }

      es.onopen = () => {
        reconnectAttempt = 0
      }
    }

    connect()

    return () => {
      clearReconnect()
      lastGroupsRevRef.current = 0
      lastWarmupSettingsRevRef.current = 0
      lastExchangeDistributorDraftRevRef.current = 0
      lastExchangeDistributorArchiveRevRef.current = 0
      if (dashboardEsRef.current) {
        try {
          dashboardEsRef.current.close()
        } catch (_) {}
        dashboardEsRef.current = null
      }
    }
  }, [authInitialized, loadFromApi, user?.id])

  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      loadFromApi()
    }, SYNC_INTERVAL_MS)
    return () => clearInterval(id)
  }, [loadFromApi])

  useEffect(() => {
    if (!authInitialized || !user || isClientRole(user)) return
    const selfKey = storageKeyForUser(user)
    const onStorage = (e) => {
      // Only react to OUR user's key — two tabs logged in as different users
      // must not bleed state into each other. This was the root cause of the
      // "all groups disappeared" report under multi-user: user B's narrower
      // filtered list written to a shared key overwrote user A's view.
      if (e.key !== selfKey || e.newValue == null) return
      if (saveInFlightRef.current) return
      if (
        dirtyRef.current &&
        Date.now() - lastLocalMutationAtRef.current < SYNC_LOCAL_GRACE_MS
      ) {
        return
      }
      let parsed
      try {
        parsed = JSON.parse(e.newValue)
      } catch {
        return
      }
      const normalized = normalizeGroupsPayload(parsed)
      if (!normalized || !isMounted.current) return
      let nextSig = ''
      try {
        nextSig = JSON.stringify(normalized)
      } catch {
        nextSig = ''
      }
      if (nextSig && nextSig === groupsSigRef.current) return
      setGroups(normalized)
      setSyncError(null)
      hasLoadedOnceRef.current = true
      dirtyRef.current = false
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [authInitialized, user])

  useEffect(() => {
    const onVisible = () => loadFromApi()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onVisible()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onVisible)
    }
  }, [loadFromApi])

  const flushBeforeUnload = useCallback(() => {
    if (!dirtyRef.current) return
    if (!hasLoadedOnceRef.current) return
    const state = groupsRef.current
    if (!state || !Array.isArray(state)) return
    if (isClientRole(user)) return
    // Never push an empty array on unload — that's how a broken tab can
    // accidentally wipe the server's view. We only persist real, non-empty
    // local state that we know was hydrated from the server earlier.
    if (state.length === 0) return
    writeGroupsToStorage(state, user)
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() }
    if (isTraderRole(user)) {
      for (const g of state) {
        dexFetch(`${API_GROUPS}/${g.id}`, {
          method: 'PATCH',
          credentials: 'include',
          body: JSON.stringify(g),
          headers,
          keepalive: true,
        }).catch(() => {})
      }
      return
    }
    dexFetch(API_GROUPS, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(state),
      headers,
      keepalive: true,
    }).catch(() => {})
  }, [getAuthHeaders, user])

  useEffect(() => {
    const onBeforeUnload = (e) => {
      flushBeforeUnload()
      if (savingRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    const onPageHide = () => flushBeforeUnload()
    window.addEventListener('beforeunload', onBeforeUnload)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [flushBeforeUnload])

  const runPersist = useCallback(async (stateToSave = null) => {
    // Must have seen a successful GET first. Without this guard, a fresh
    // tab that failed to hydrate could push [] and wipe the server.
    if (!hasLoadedOnceRef.current) return
    if (isClientRole(user)) return
    const toSave = stateToSave ?? groupsRef.current
    if (!toSave || !Array.isArray(toSave) || toSave.length === 0) return
    saveInFlightRef.current = true
    if (isMounted.current) setSaving(true)
    let lastErr
    for (let attempt = 0; attempt < PERSIST_RETRIES; attempt++) {
      try {
        if (isTraderRole(user)) {
          const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() }
          let allOk = true
          for (const g of toSave) {
            const res = await dexFetch(`${API_GROUPS}/${g.id}`, {
              method: 'PATCH',
              credentials: 'include',
              headers,
              body: JSON.stringify(g),
            })
            if (!res.ok) {
              allOk = false
              lastErr = new Error(res.statusText || `PATCH ${g.id}`)
            }
          }
          if (allOk) {
            if (isMounted.current) {
              setSyncError(null)
              dirtyRef.current = false
            }
            writeGroupsToStorage(toSave, user)
            break
          }
        } else {
          const res = await dexFetch(API_GROUPS, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(toSave),
          })
          if (res.ok) {
            if (isMounted.current) {
              setSyncError(null)
              dirtyRef.current = false
            }
            writeGroupsToStorage(toSave, user)
            break
          }
          lastErr = new Error(res.statusText)
        }
      } catch (e) {
        lastErr = e
      }
      if (attempt < PERSIST_RETRIES - 1) {
        await new Promise((r) => setTimeout(r, PERSIST_RETRY_DELAY_MS))
      }
    }
    if (lastErr && isMounted.current) {
      if (isPlatformAvailabilityFailure(lastErr)) setSyncError(null)
      else setSyncError(lastErr?.message || 'No connection to server')
    }
    saveInFlightRef.current = false
    if (isMounted.current) setSaving(false)
    if (pendingSaveRef.current && isMounted.current) {
      pendingSaveRef.current = false
      runPersist()
    }
  }, [getAuthHeaders, user])

  const saveGroups = useCallback((next) => {
    const newState = typeof next === 'function' ? next(groupsRef.current) : next
    lastLocalMutationAtRef.current = Date.now()
    dirtyRef.current = true
    setGroups(newState)
    if (saveInFlightRef.current) {
      pendingSaveRef.current = true
    } else {
      runPersist(newState)
    }
  }, [runPersist])

  const addGroup = useCallback((group) => {
    const newGroup = {
      id: crypto.randomUUID(),
      name: group.name,
      tokenContract: group.tokenContract,
      network: group.network,
      rpc: group.rpc,
      bots: [],
      wallets: [],
      targetVolume: null,
      walletsScrollAfter: group.walletsScrollAfter ?? 10,
      swapPairToken: group.swapPairToken && String(group.swapPairToken).trim() ? group.swapPairToken : null,
      liquidityPoolAddress:
        group.liquidityPoolAddress && String(group.liquidityPoolAddress).trim()
          ? String(group.liquidityPoolAddress).trim()
          : '',
      createdAt: Date.now(),
    }
    saveGroups((prev) => [...prev, newGroup])
    return newGroup.id
  }, [saveGroups])

  const updateGroup = useCallback((id, updates) => {
    saveGroups((prev) =>
      prev.map((g) => (g.id === id ? { ...g, ...updates } : g))
    )
  }, [saveGroups])

  const removeGroup = useCallback((id) => {
    saveGroups((prev) => prev.filter((g) => g.id !== id))
  }, [saveGroups])

  const getGroup = useCallback((id) => {
    return groups.find((g) => g.id === id) ?? null
  }, [groups])

  const addBotToGroup = useCallback((groupId, { algorithmId, name, config = {} }) => {
    const bot = {
      id: crypto.randomUUID(),
      algorithmId,
      name: name || algorithmId,
      config,
      addedAt: Date.now(),
    }
    saveGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, bots: [...(g.bots || []), bot] } : g
      )
    )
    return bot.id
  }, [saveGroups])

  const addBotsToGroup = useCallback((groupId, botsToAdd) => {
    const newBots = botsToAdd.map(({ algorithmId, name, config = {} }) => ({
      id: crypto.randomUUID(),
      algorithmId,
      name: name || algorithmId,
      config,
      addedAt: Date.now(),
    }))
    saveGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, bots: [...(g.bots || []), ...newBots] } : g
      )
    )
    return newBots.map((b) => b.id)
  }, [saveGroups])

  const removeBotFromGroup = useCallback((groupId, botId) => {
    saveGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, bots: (g.bots || []).filter((b) => b.id !== botId) }
          : g
      )
    )
  }, [saveGroups])

  const removeBotsFromGroup = useCallback((groupId, botIds) => {
    const idSet = new Set(Array.isArray(botIds) ? botIds : [botIds])
    if (idSet.size === 0) return
    saveGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, bots: (g.bots || []).filter((b) => !idSet.has(b.id)) }
          : g
      )
    )
  }, [saveGroups])

  const updateGroupBot = useCallback((groupId, botId, updates) => {
    saveGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g
        const bots = (g.bots || []).map((b) =>
          b.id === botId ? mergeBotUpdates(b, updates) : b
        )
        return { ...g, bots }
      })
    )
  }, [saveGroups])

  const updateGroupBots = useCallback((groupId, botIds, updates) => {
    const idSet = new Set(botIds)
    saveGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g
        const bots = (g.bots || []).map((b) =>
          idSet.has(b.id) ? mergeBotUpdates(b, updates) : b
        )
        return { ...g, bots }
      })
    )
  }, [saveGroups])

  const updateGroupBotsBatch = useCallback((groupId, botUpdates) => {
    const byId = new Map(Array.isArray(botUpdates) ? botUpdates.map((u) => [u.botId, u.updates]) : [])
    if (byId.size === 0) return
    saveGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g
        const bots = (g.bots || []).map((b) => {
          const u = byId.get(b.id)
          return u ? mergeBotUpdates(b, u) : b
        })
        return { ...g, bots }
      })
    )
  }, [saveGroups])

  const createWalletInGroup = useCallback(
    async (groupId, { type, label }) => {
      const res = await dexFetch(`/api/groups/${groupId}/wallets`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ type, label: label || undefined }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || res.statusText)
      }
      const wallet = await res.json()
      saveGroups((prev) =>
        prev.map((g) =>
          g.id !== groupId ? g : { ...g, wallets: [...(g.wallets || []), wallet] }
        )
      )
      return wallet
    },
    [getAuthHeaders, saveGroups]
  )

  const createWalletAndAssignToBots = useCallback(
    async (groupId, { type, label }, botIds) => {
      const res = await dexFetch(`/api/groups/${groupId}/wallets`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ type, label: label || undefined }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || res.statusText)
      }
      const wallet = await res.json()
      const idSet = new Set(Array.isArray(botIds) ? botIds : [])
      saveGroups((prev) =>
        prev.map((g) => {
          if (g.id !== groupId) return g
          const bots = (g.bots || []).map((b) =>
            idSet.has(b.id)
              ? { ...b, config: { ...(b.config || {}), walletId: wallet.id } }
              : b
          )
          return { ...g, wallets: [...(g.wallets || []), wallet], bots }
        })
      )
      return wallet
    },
    [getAuthHeaders, saveGroups]
  )

  const updateWalletInGroup = useCallback(
    async (groupId, walletId, updates) => {
      const res = await dexFetch(`/api/groups/${groupId}/wallets/${walletId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || res.statusText)
      }
      const wallet = await res.json()
      saveGroups((prev) =>
        prev.map((g) => {
          if (g.id !== groupId) return g
          const wallets = (g.wallets || []).map((w) =>
            w.id === walletId ? { ...w, ...wallet } : w
          )
          let bots = g.bots || []
          if (
            wallet.type === 'sub' &&
            wallet.status === 'worked' &&
            Array.isArray(bots) &&
            bots.length
          ) {
            bots = bots.map((b) => {
              if (b.algorithmId !== 'dca-bot') return b
              const cfg = b.config || {}
              if (cfg.walletId !== walletId) return b
              const { walletId: _omit, ...restCfg } = cfg
              return { ...b, config: restCfg }
            })
          }
          return { ...g, wallets, bots }
        })
      )
      return wallet
    },
    [getAuthHeaders, saveGroups]
  )

  return (
    <GroupsContext.Provider
      value={{
        groups,
        loading,
        saving,
        syncError,
        refetchGroups: loadFromApi,
        flushBeforeUnload,
        addGroup,
        updateGroup,
        removeGroup,
        getGroup,
        addBotToGroup,
        addBotsToGroup,
        removeBotFromGroup,
        removeBotsFromGroup,
        updateGroupBot,
        updateGroupBots,
        updateGroupBotsBatch,
        createWalletInGroup,
        createWalletAndAssignToBots,
        updateWalletInGroup,
      }}
    >
      {children}
    </GroupsContext.Provider>
  )
}

export function useGroups() {
  const ctx = useContext(GroupsContext)
  if (!ctx) throw new Error('useGroups must be used within GroupsProvider')
  return ctx
}
